import { type HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { aadConfig, getAccessToken } from '@services/aad';
import {
  getAccountUsers,
  getMonthlyProjectCounts,
  getProEst,
  getProject,
  getProjectBasic,
  getProjectCounts,
  getProjectNames,
  patchProject,
} from '@services/api';
import { type Account, AccountType, ResourceUserRole, type User } from '@services/dto/account';
import type { Patch } from '@services/dto/common';
import { type Project, type ProjectCounts, ProjectStatus } from '@services/dto/project';
import {
  UserType,
  useCurrentUserAccount,
  useCurrentUserOffice,
  useIsProjectScopedGuest,
  useMe,
  useRights,
  useUserOffices,
} from '@services/state';
import { resolveProjectManagers, shouldReadCompanyRosters } from '@services/projectScopedGuest';
import type { PhaseEx, ProjectEx } from '@services/state/project/types';
import { useSaving } from '@services/state/saving';
import { useAppDispatch, useAppSelector } from '@services/state/store';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as uuid from 'uuid';
import { projectActions } from './slice';

export type UseEditProjectHook<T extends Project> = {
  project: T;
  phase?: PhaseEx;
  load: (accountId: string, projectId: string, asyncUpdate?: boolean) => Promise<T>;
  patch: (patch: Patch[], noDebounce?: boolean) => Promise<T>;
  setProject: (project: T) => void;
  refresh: () => Promise<T>;
  loading: boolean;
  error?: DOMException;
};

export type PatchOptions = {
  id: string;
  added: number;
  accountId: string;
  projectId: string;
  patch: Patch[];
  l?: string;
};
type PatchTask<T = unknown> = {
  options: PatchOptions;
  pending: Promise<T>;
};
const patchTasks: PatchTask[] = [];

export function useEditProject<T extends ProjectEx>(): UseEditProjectHook<T> {
  const { t } = useTranslation();
  const [account] = useCurrentUserAccount();

  const state = useAppSelector((state) => state.project);
  const dispatch = useAppDispatch();

  const {
    i18n: { language },
  } = useTranslation();
  const [, setSaving] = useSaving();
  const rights = useRights();
  const [me] = useMe();
  const { phaseId } = useParams<{ phaseId?: string }>();

  const [phase, setPhase] = React.useState<PhaseEx>();
  const projectScopedGuest = useIsProjectScopedGuest();

  const initialize = React.useCallback(
    (project: T): T => {
      if (!project) return project;

      project.canEdit = false;
      project.canEditPhase = false;
      project.canDelete = false;
      project.canInvite = false;
      project.canViewStaff = false;
      project.canComplete = false;

      /*
       * Resolved by resolveProjectManagers so the rule has one home and is tested there: the roster
       * confirms that the Owner named on the project is still on the company, an absent roster
       * cannot confirm and falls back to the project's own record, and an empty roster matches
       * nobody. Collapsing absent and empty is how a reader who may not see a roster would silently
       * lose the names the project itself carries.
       */
      const managers = resolveProjectManagers({
        users: project.users,
        accountRoster: project.accountWithUsers?.users,
        providerRoster: project.providerAccountWithUsers?.users,
        providerPmUserId: project.providerPmUserId,
      });
      project.clientPm = managers.clientPm as typeof project.clientPm;
      project.providerPm = managers.providerPm as typeof project.providerPm;

      // Find a user in this project
      let pu = project.users?.find((u) => u.id === me?.id);
      if (!pu) {
        project.phases.forEach((ph) => {
          pu = ph.users?.find((u) => u.id === me?.id);
          if (!pu) {
            ph.estimates?.forEach((est) => {
              pu = est.labor?.find((l) => l.userId === me?.id)?.user;
            });
          }
        });
      }

      let phaseVisible = false;
      let providerUser: User | undefined;

      if (rights?.userType === UserType.provider) {
        // Without the roster this resolves to nobody, so canEdit and canComplete below stay false
        // unless the reader is the primary provider. A refused roster removes rights, never grants.
        pu = project.providerAccountWithUsers?.users.find((u) => u.id === me?.id);
        providerUser = rights?.primaryProvider ? me : pu;
        project.canEdit =
          rights?.primaryProvider ||
          ((pu?.role === ResourceUserRole.Owner || pu?.role === ResourceUserRole.Administrator) &&
            ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown));
        project.canEditPhase = project.canEdit;

        project.canComplete =
          rights?.primaryProvider || pu?.role === ResourceUserRole.Owner || pu?.role === ResourceUserRole.Administrator;

        project.canDelete = rights?.primaryProvider || (pu?.role === ResourceUserRole.Owner && project.canEdit);
        project.canInvite = project.canEdit;
        project.canViewStaff = true;
        phaseVisible = true;
      } else if (pu) {
        project.canEdit =
          (pu.role === ResourceUserRole.Owner || pu.role === ResourceUserRole.Administrator) &&
          ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown);
        project.canEditPhase = project.canEdit;

        project.canViewStaff = pu.role === ResourceUserRole.Owner || pu.role === ResourceUserRole.Administrator;
        project.canInvite =
          project.canViewStaff &&
          ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown);

        project.canDelete = pu.role === ResourceUserRole.Owner && project.canEdit;
        project.isGuest = pu.role === ResourceUserRole.Guest;
        phaseVisible = true;
      } else {
        const ou = project.office?.users?.find((x) => x.id === me?.id);
        if (ou) {
          project.canEdit =
            (ou.role === ResourceUserRole.Owner || ou.role === ResourceUserRole.Administrator) &&
            ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown);
          project.canEditPhase = project.canEdit;
          project.canInvite = project.canEdit;
          project.canViewStaff = true;

          project.canDelete = ou.role === ResourceUserRole.Owner && project.canEdit;
          project.isGuest = ou.role === ResourceUserRole.Guest;
          phaseVisible = true;
        } else {
          const au = project.account?.users?.find((x) => x.id === me?.id);
          if (au) {
            project.canEdit =
              (au.role === ResourceUserRole.Owner || au.role === ResourceUserRole.Administrator) &&
              ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown);
            project.canEditPhase = project.canEdit;
            project.canInvite = project.canEdit;
            project.canViewStaff = true;

            project.canDelete = au.role === ResourceUserRole.Owner && project.canEdit;
            project.isGuest = au.role === ResourceUserRole.Guest;
            phaseVisible = true;
          }
        }
      }

      if (rights?.userType === UserType.expert) {
        project.canEdit = false;
        project.canEditPhase = false;
        project.canInvite = false;
        project.canViewStaff = false;
        project.canDelete = false;
      }

      // Roles doc §11: the assigned Project Manager (a Contributor whose company position is
      // "Project Manager") may build the team on projects they are assigned to. Scoped to that
      // person + this project; mirrors the BOE isProviderPm pattern. Estimators and every other
      // Contributor stay locked out (the expert strip above still holds for them), and this never
      // grants canEdit/canDelete - the PM manages the team, not the project.
      const providerMember = project.providerAccountWithUsers?.users.find((u) => u.id === me?.id);
      const isAssignedProjectManager =
        providerMember?.role === ResourceUserRole.Contributor &&
        providerMember?.providerPosition === 'Project Manager' &&
        (project.providerPmUserId === me?.id || !!pu);
      if (
        isAssignedProjectManager &&
        ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(project.status ?? ProjectStatus.Unknown)
      ) {
        project.canViewStaff = true;
        project.canInvite = true;
        project.isAssignedPm = true;
      }

      project.canSubmit = project.canEdit && project.status === ProjectStatus.Draft && project.phases.length > 0;

      if (project.canSubmit) {
        if (
          !project.budget ||
          (project.budget.isFixed && !project.budget.fixed) ||
          (!project.budget.isFixed && !project.budget.range)
        )
          project.canSubmit = false;
      }

      let startPhaseId: string;
      let canStart = project.status === ProjectStatus.InProgress;
      project.phases?.forEach((ph) => {
        ph.canStart = false;

        if (ph.status === ProjectStatus.InProgress) canStart = false;
        if (canStart && !startPhaseId && ph.status === ProjectStatus.Approved) {
          startPhaseId = ph.id ?? 'unknown';
          ph.canStart = true;
        }

        if (providerUser) {
          ph.canEdit = project.canEdit;
          ph.canDelete = project.canDelete;
          ph.isGuest = project.isGuest;
          ph.canInvite = project.canInvite;
          ph.canViewStaff = project.canViewStaff;
          ph.isVisible = true;

          if (providerUser.role === ResourceUserRole.Owner || providerUser.role === ResourceUserRole.Administrator) {
            ph.canEdit = project.canEdit;
            ph.canDelete = project.canDelete;
            ph.canComplete = true;
          }

          if (rights?.userType === UserType.provider) {
            if ([ProjectStatus.Complete, ProjectStatus.Canceled].includes(ph.status ?? ProjectStatus.Unknown)) {
              ph.canEdit = false;
              ph.canDelete = false;
            }
          } else if (![ProjectStatus.New, ProjectStatus.Submitted].includes(ph.status ?? ProjectStatus.Unknown)) {
            ph.canEdit = false;
            ph.canDelete = false;
          }

          ph.estimates?.forEach((est) => {
            if (
              est.labor.filter((l) => ![ProjectStatus.Complete, ProjectStatus.Canceled].includes(l.status)).length > 0
            )
              ph.canComplete = false;

            if (providerUser.role === ResourceUserRole.Owner || providerUser.role === ResourceUserRole.Administrator) {
              est.canEdit = ph.canEdit;
              est.isVisible = true;
            } else {
              est.canEdit = false;
              est.isVisible = false;

              if (providerUser.role === ResourceUserRole.Contributor) {
                const l = est.labor?.find((l) => l.user?.id === providerUser.id);
                if (l) {
                  est.canEdit = false;
                  est.isVisible = true;
                }
              } else if (providerUser.role === ResourceUserRole.Guest) {
                est.canEdit = false;
                est.isVisible = true;
              }
            }
          });

          if (!ph.canComplete) project.canComplete = false;
        } else {
          const u = ph.users?.find((x) => x.id === me?.id);
          if (u) {
            ph.isGuest = u.role === ResourceUserRole.Guest || project.isGuest;
            ph.canEdit =
              !ph.isGuest && (u.role === ResourceUserRole.Owner || u.role === ResourceUserRole.Administrator);
            ph.canInvite = ph.canEdit;

            ph.canDelete = !ph.isGuest && u.role === ResourceUserRole.Owner;
            ph.isVisible = true;
          } else {
            ph.isGuest = project.isGuest;
            ph.canEdit = !ph.isGuest && project.canEdit;
            ph.canInvite = !ph.isGuest && project.canInvite;
            ph.canViewStaff = project.canViewStaff;
            ph.canDelete = !ph.isGuest && project.canDelete;
            ph.isVisible = phaseVisible;
          }
        }

        ph.canSubmit =
          !ph.isGuest &&
          project.status !== ProjectStatus.Draft &&
          ph.canEdit &&
          ph.status === ProjectStatus.New &&
          !!project.budget;
      });

      if (rights?.userType === UserType.expert) {
        project.phases.forEach((ph) => {
          ph.estimates?.forEach((est) => {
            est.labor
              ?.filter((l) => l.userId === me?.id)
              ?.forEach((l) => {
                project.expertOffer = true;

                if (l.status === ProjectStatus.FeeReady) {
                  const sh = l.statusHistory.find((sh) => sh.newStatus === ProjectStatus.FeeReady);
                  const exp = sh?.createdAt
                    ? moment.duration(60 * 60 * 24 * 1000 - (moment().valueOf() - moment(sh.createdAt).valueOf()))
                    : null;

                  project.expertOfferPending = (exp?.asMilliseconds() || 0) > 0;
                }
              });
          });
        });
      }

      return project;
    },
    [me, rights, phaseId, t],
  );

  const finishLoad = React.useCallback(
    async (p: T, force?: boolean): Promise<T> => {
      if (!force && state.project?.account?.id === p?.account?.id) {
        p.account = state.project.account;
        p.accountWithUsers = state.project.accountWithUsers;
        p.providerAccountWithUsers = state.project.providerAccountWithUsers;

        const px = initialize(p);
        dispatch(projectActions.setProject(px));
        return px;
      }

      /*
       * The rosters are for display - they resolve which of the project's own people are still on
       * each company - so a refusal degrades two names, not the page. They used to be fetched with
       * Promise.all, which made one 403 reject the whole load: the project itself had already come
       * back 200 and was thrown away.
       *
       * A project-scoped guest is not asked at all. ProjectScopedGuestFilter refuses anything whose
       * route carries an accountId and never looks at the query string, so a projectId on this call
       * would change nothing - the reads that do carry one and succeed are Project.Api calling the
       * account service as itself, not the browser.
       */
      const [accountRoster, providerRoster] = !shouldReadCompanyRosters(projectScopedGuest)
        ? [undefined, undefined]
        : (await Promise.allSettled([getAccountUsers(p.account.id), getAccountUsers(p.provider.id)])).map(
            (r, i) => {
              if (r.status === 'fulfilled') return r.value;
              console.warn(
                '[project-load] roster unavailable; names on this project may be incomplete',
                i === 0 ? 'account' : 'provider',
                r.reason instanceof Error ? r.reason.message : r.reason,
              );
              return undefined;
            },
          );

      p.accountWithUsers = accountRoster;
      p.providerAccountWithUsers = providerRoster;

      /*
       * TEMPORARY - remove with the fix for the project-scoped guest workspace.
       *
       * initialize() reads accountWithUsers.users, providerAccountWithUsers.users and phases
       * without guarding any of them. Every request on open returns 200, so whatever is missing is
       * missing from a payload, not from a refusal. Shape only: which fields arrived and how many
       * entries they hold. No names, no ids, no project data.
       */
      console.log(
        '[project-load-diag]',
        `accountWithUsers=${!!p.accountWithUsers}`,
        `accountWithUsers.users=${p.accountWithUsers?.users ? p.accountWithUsers.users.length : 'absent'}`,
        `providerAccountWithUsers=${!!p.providerAccountWithUsers}`,
        `providerAccountWithUsers.users=${p.providerAccountWithUsers?.users ? p.providerAccountWithUsers.users.length : 'absent'}`,
        `phases=${p.phases ? p.phases.length : 'absent'}`,
        `users=${p.users ? p.users.length : 'absent'}`,
        `account=${!!p.account}`,
        `provider=${!!p.provider}`,
      );

      const ready = initialize(p);
      dispatch(projectActions.setProject(ready));
      return ready;
    },
    [state.project, phaseId],
  );

  const load = React.useCallback(async (accountId: string, projectId: string, asyncUpdate?: boolean): Promise<T> => {
    dispatch(projectActions.setLoading(true));
    try {
      let p: ProjectEx;
      try {
        p = await getProject(accountId, projectId, language);
      } catch (loadErr) {
        // The active dashboard account may not own this project - a user with access to
        // several accounts can see a project in one account's list while it is owned by
        // another (getProject is keyed by the owning account). On a not-found/forbidden,
        // resolve the project's owning account once and retry before surfacing a failure.
        const status = (loadErr as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 403) {
          const basic = await getProjectBasic(projectId);
          if (basic?.accountId && basic.accountId !== accountId) {
            p = await getProject(basic.accountId, projectId, language);
          } else {
            throw loadErr;
          }
        } else {
          throw loadErr;
        }
      }
      p.asyncUpdate = asyncUpdate;
      return finishLoad(JSON.parse(JSON.stringify(p)) as T);
    } catch (err) {
      dispatch(projectActions.setError(err));
      throw err;
    }
  }, []);

  const patch = React.useCallback(
    (patch: Patch[], noDebounce?: boolean): Promise<T> => {
      if (!state.project?.id) return Promise.reject(new Error('No project loaded'));
      if (!account?.id || !state.project?.account) return Promise.reject(new Error('No project account loaded'));

      const accountId = state.project?.account?.id || account.id;

      let task = patchTasks.find((x) => x.options.projectId === state.project?.id);
      if (task) {
        // Replace or add a patch item
        patch.forEach((p) => {
          if (!task) return;
          const idx = task.options.patch.findIndex((x) => x.propertyName === p.propertyName);
          if (idx >= 0) task.options.patch[idx] = p;
          else task.options.patch.push(p);
        });
        task.options.added = Date.now();
        return task.pending as Promise<T>;
      }

      const stage = (options: PatchOptions) =>
        new Promise<PatchOptions>((resolve) => {
          if (noDebounce) {
            setSaving(true);
            resolve(options);
            return;
          }

          const debounce = () => {
            if (Date.now() - options.added < 500) {
              setTimeout(debounce, 500);
              return;
            }
            setSaving(true);
            resolve(options);
          };
          setTimeout(debounce, 500);
        })
          .then((options) => {
            const idx = patchTasks.findIndex((x) => x.options.id === options.id);
            if (idx >= 0) patchTasks.splice(idx, 1);
            return patchProject(options.accountId, options.projectId, options.patch, options.l);
          })
          .then((p) => finishLoad(p as T))
          .finally(() => setSaving(false));

      const options: PatchOptions = {
        id: uuid.v4(),
        added: Date.now(),
        accountId,
        projectId: state.project.id,
        patch: [...patch],
        l: language,
      };
      task = { options, pending: stage(options) };
      patchTasks.push(task);
      return task.pending as Promise<T>;
    },
    [state.project, account],
  );

  const setProject = React.useCallback((project: T) => {
    finishLoad(project, true).finally();
  }, []);

  const refresh = React.useCallback(async (): Promise<T> => {
    if (!state.project?.id || !state.project?.account?.id) {
      console.error('Cannot refresh: No project loaded');
      throw new Error('Cannot refresh: No project loaded');
    }
    return await load(state.project.account.id, state.project.id, state.project.asyncUpdate);
  }, [state.project]);

  React.useEffect(() => {
    if (!state.project) {
      setPhase(undefined);
      return;
    }
    setPhase(phaseId ? state.project.phases?.find((ph) => ph.id === phaseId) : undefined);
  }, [phaseId, state.project]);

  return {
    project: state.project as T,
    phase,
    load,
    patch,
    setProject,
    refresh,
    loading: !!state.loading,
    error: state.error,
  };
}

export function useProjectCounts(): ProjectCounts[] {
  const state = useAppSelector((state) => state.project.counts);
  const dispatch = useAppDispatch();

  const [account] = useCurrentUserAccount();
  const [offices] = useUserOffices();
  const [office] = useCurrentUserOffice();

  // Keyed on what the request is actually made of, not on the objects carrying
  // it. The account, the office list and the current office are all rebuilt in
  // the store more often than they change, and this effect was re-running - and
  // issuing a fresh aggregate request - on every one of those rebuilds. The API
  // dedupe only covers calls still in flight, so each wave reached the server.
  const accountId = account?.id;
  const accountType = account?.type;
  const officeId = office?.id;
  const officeKey = (offices ?? []).map((o) => o.id).join(',');
  const projectScopedGuest = useIsProjectScopedGuest();

  React.useEffect(() => {
    if (!accountId) return;

    /*
     * Account-wide statistics are an account-scoped read, which a project-scoped guest is refused -
     * correctly. Asking anyway produced a 403 and, with no rejection handler, an uncaught
     * AxiosError in the console on every visit to the projects list. A guest's own figures are
     * computed by the list from the projects it already has.
     */
    if (projectScopedGuest) return;

    const officeIds = officeId ? [officeId] : undefined;
    getProjectCounts(accountId, officeIds, accountType === AccountType.Provider ? accountId : undefined)
      .then((counts) => {
        dispatch(projectActions.setCounts(counts));
      })
      .catch((err) => {
        // Reported rather than thrown into the void: an unhandled rejection here is noise that
        // hides real failures, and the counters simply stay as they were.
        console.warn('[project-counts] could not load account statistics', err instanceof Error ? err.message : err);
      });
  }, [accountId, accountType, officeKey, officeId, projectScopedGuest]);

  return state;
}

export function useMonthlyProjectCounts(): ProjectCounts[] {
  const state = useAppSelector((state) => state.project.monthlyCounts);
  const dispatch = useAppDispatch();

  const [account] = useCurrentUserAccount();

  // The monthly aggregate is account-wide - it never took the office into
  // account - so it is keyed on the account id alone. It was listed against the
  // office objects too, which only re-fired it.
  const accountId = account?.id;
  const accountType = account?.type;

  React.useEffect(() => {
    if (!accountId) return;

    getMonthlyProjectCounts(accountId, accountType === AccountType.Provider ? accountId : undefined).then((counts) => {
      dispatch(projectActions.setMonthlyCounts(counts));
    });
  }, [accountId, accountType]);

  return state;
}

export interface ProjectWatcherParams {
  accountId?: string;
  onNew?: (accountId: string, projectId: string, byUserId: string) => void;
  onUpdate?: (accountId: string, projectId: string, byUserId: string) => void;
  onDelete?: (accountId: string, projectId: string, byUserId: string) => void;
}

export interface ProjectWatcherHook {
  accountId?: string;
  setAccount: (account?: Account | string) => void;
}

export const useProjectWatcher = ({
  accountId: initAccountId,
  onNew,
  onUpdate,
  onDelete,
}: ProjectWatcherParams): ProjectWatcherHook => {
  const [connection, setConnection] = React.useState<HubConnection>();

  const [accountId, setAccountId] = React.useState<string | undefined>(initAccountId);
  const setAccount = React.useCallback((account?: Account | string) => {
    if (typeof account === 'string') setAccountId(account);
    else setAccountId(account?.id);
  }, []);

  const handleNew = React.useCallback(
    (accountId: string, projectId: string, byUserId: string) => {
      if (onNew) onNew(accountId, projectId, byUserId);
    },
    [onNew],
  );

  const handleUpdate = React.useCallback(
    (accountId: string, projectId: string, byUserId: string) => {
      if (onUpdate) onUpdate(accountId, projectId, byUserId);
    },
    [onUpdate],
  );

  const handleDelete = React.useCallback(
    (accountId: string, projectId: string, byUserId: string) => {
      if (onDelete) onDelete(accountId, projectId, byUserId);
    },
    [onDelete],
  );

  const handleConnected = React.useCallback(() => {}, [connection]);

  React.useEffect(() => {
    if (!accountId) return undefined;

    setTimeout(() => {
      setConnection((current) => {
        if (current && current.state !== HubConnectionState.Disconnected) return current;

        // Create a connection instance
        return new HubConnectionBuilder()
          .withUrl(`${process.env.PROJECT_API}/projects?aid=${accountId}`, {
            accessTokenFactory: () => getAccessToken(aadConfig().projectScopes).then((r) => r as string),
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();
      });
    });

    return () => {
      setConnection((current) => {
        if (current && current.state === HubConnectionState.Connected) {
          current.stop().then(() => {});
        }
        return undefined;
      });
    };
  }, [accountId, setConnection]);

  const [updateAt, setUpdatedAt] = React.useState(0);
  const updateAtRef = React.useRef(0);
  updateAtRef.current = updateAt;

  React.useEffect(() => {
    if (!connection || connection.state !== HubConnectionState.Disconnected) return;

    // Start the connection
    connection.start().then(() => {
      if (!connection) return;

      connection.on('RecvNew', (accountId: string, projectId: string, byUserId: string, _updateAt: number) => {
        if (updateAtRef.current !== _updateAt) {
          handleNew(accountId, projectId, byUserId);
          setUpdatedAt(_updateAt);
        }
      });
      connection.on('RecvUpdate', (accountId: string, projectId: string, byUserId: string, _updateAt: number) => {
        if (updateAtRef.current !== _updateAt) {
          handleUpdate(accountId, projectId, byUserId);
          setUpdatedAt(_updateAt);
        }
      });
      connection.on('RecvDelete', (accountId: string, projectId: string, byUserId: string, _updateAt: number) => {
        if (updateAtRef.current !== _updateAt) {
          handleDelete(accountId, projectId, byUserId);
          setUpdatedAt(_updateAt);
        }
      });

      handleConnected();
    });
  }, [accountId, connection, updateAt]);

  return {
    accountId,
    setAccount,
  };
};

export const useProjectNames = () => {
  const [account] = useCurrentUserAccount();
  const rights = useRights();
  const [loading, setLoading] = React.useState<boolean>(false);

  const state = useAppSelector((state) => state.project);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (!account?.id) return;
    if (rights?.userType !== UserType.provider) {
      dispatch(projectActions.setProjectNames([]));
      return;
    }

    setLoading(true);

    getProjectNames(account?.id)
      .then((names) => {
        dispatch(projectActions.setProjectNames(names));
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [account, rights]);

  return {
    accountId: account?.id,
    names: state.projectNames ?? [],
    loading,
  };
};

export const useProEst = (project?: ProjectEx, phase?: PhaseEx) => {
  const [proEstId, setProEstId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const state = useAppSelector((state) => state.project);
  const dispatch = useAppDispatch();

  const load = React.useCallback(() => {
    if (!project?.account || !phase) return;

    if (!phase.proEst) {
      setProEstId(undefined);
      dispatch(projectActions.setProEst(undefined));
      setLoading(false);
      return;
    }

    setProEstId(phase.proEst.id);
    setLoading(true);
    getProEst(project.account.id, project.id ?? 'unknown', phase.id ?? 'unknown')
      .then((result) => {
        dispatch(projectActions.setProEst(result));
      })
      .finally(() => setLoading(false));
  }, [project, phase]);

  useEffect(() => load(), [project, phase]);

  return {
    proEstId,
    load,
    estimate: state.proEst?.estimate,
    details: state.proEst?.details,
    loading,
  };
};

export const useExcelEstimate = (project?: ProjectEx, phase?: PhaseEx) => {
  const [loading, setLoading] = useState(true);

  const state = useAppSelector((state) => state.project);
  const dispatch = useAppDispatch();

  // Load the phase estimate from the authoritative server read. Imported/entered rows are
  // saved to the proest details store, so read them back from there; fall back to any estimate
  // embedded on the phase. Fixes the grid showing empty after a successful import.
  const load = React.useCallback(() => {
    if (!project?.account?.id || !project?.id || !phase?.id) {
      dispatch(projectActions.setExcel(phase?.excelEstimate));
      setLoading(false);
      return;
    }
    setLoading(true);
    getProEst(project.account.id, project.id, phase.id)
      .then((res) => {
        const details = res?.details?.items?.length ? res.details : phase?.excelEstimate;
        dispatch(projectActions.setExcel(details));
      })
      .catch(() => dispatch(projectActions.setExcel(phase?.excelEstimate)))
      .finally(() => setLoading(false));
  }, [project, phase]);

  React.useEffect(() => load(), [project, phase]);

  return {
    details: state.excel,
    loading,
  };
};
